/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useMemo, useState } from 'react'
import { Box, Button, Heading, Txt, List, ListItem, FormattedCurrency } from '../../components'
import { Modal } from '../../components/Modal'
import { ClientGameHandController } from '../../controllers/ClientGameHandController'
import { FormattedNumber } from '../../components/FormattedNumber'
import { format } from 'date-fns'
import { FormattedTime } from '../../components/FormattedTime'
import _ from 'lodash'

export const GameFAQModal: React.FC<{ controller: ClientGameHandController }> = ({
  controller,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { gameHand, game } = controller
  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label="OurPokerGame FAQs"
        header={
          <React.Fragment>
            <Heading as="h3" sx={{ mb: 2 }}>
              Game Details
            </Heading>
            {[
              {
                label: 'Total Table Chips',
                value: <FormattedCurrency amount={controller.totalChips} />,
              },
              !_.isNil(gameHand.rules.numberOfHandsUntilBlindIncrease) && {
                label: 'Blinds',
                value: (
                  <React.Fragment>
                    <FormattedCurrency amount={gameHand.rules.blindAmount / 2} />/
                    <FormattedCurrency amount={gameHand.rules.blindAmount} /> - increase in{' '}
                    <FormattedNumber amount={gameHand.rules.numberOfHandsUntilBlindIncrease} />{' '}
                    hands
                  </React.Fragment>
                ),
              },
              {
                label: 'Game',
                value: (
                  <React.Fragment>
                    {gameHand.rules.gameType === 'TEXAS_HOLDEM_NO_LIMIT' ? 'NL' : 'Limit'} Holdem -
                    started {format(new Date(game.datetimeCreated), 'LLL do')} at{' '}
                    <FormattedTime format="K:mm aaa" datetime={new Date(game.datetimeCreated)} />
                  </React.Fragment>
                ),
              },
            ].map((content, index) =>
              content ? (
                <Txt sx={{ fontSize: [1, 1, 2] }} key={index}>
                  <Txt as="span" sx={{ color: 'lightGreen' }}>
                    {content.label} -{' '}
                  </Txt>
                  <Txt as="span">{content.value}</Txt>
                </Txt>
              ) : null,
            )}
          </React.Fragment>
        }
      >
        <Heading as="h3">OurPokerGame FAQs</Heading>
        {[
          {
            header: "How do i force my friends to go when it's their turn?",
            content: (
              <React.Fragment>
                <Txt>
                  You can set your status to "away". This will automatically check/fold your hand
                  when it's your turn.
                </Txt>
                <Txt sx={{ mt: 2 }}>
                  If you'd like to set someone else to "away", click on their profile picture. From
                  there you can vote to automatically set them to away. Once more than half the
                  table has voted to, their hands will be auto-folded/checked until they return.
                </Txt>
              </React.Fragment>
            ),
          },
          {
            header: "I've lost all my chips. Can I keep playing?",
            content: (
              <React.Fragment>
                Table admins (usually the person who created the game), can give players additional
                chips by clicking on their profile pictures and pressing the "Give additional chips"
                button.
              </React.Fragment>
            ),
          },
          {
            header: 'Are there keyboard shortcuts?',
            content: (
              <React.Fragment>
                <Txt>Yes! the following keyboard shortcuts work:</Txt>
                <List>
                  <ListItem>
                    <Txt as="span" sx={{ fontWeight: 'bold' }}>
                      "C"
                    </Txt>{' '}
                    - focus on the "Call/Check" action button
                  </ListItem>
                  <ListItem>
                    <Txt as="span" sx={{ fontWeight: 'bold' }}>
                      "F"
                    </Txt>{' '}
                    - focus on the "Fold" action button
                  </ListItem>
                  <ListItem>
                    <Txt as="span" sx={{ fontWeight: 'bold' }}>
                      "R"
                    </Txt>{' '}
                    - focus on the "Raise" number input
                  </ListItem>
                </List>
              </React.Fragment>
            ),
          },
        ].map(({ header, content }, index) => (
          <Box as="section" key={index} sx={{ mt: 3 }}>
            <Heading as="h4" sx={{ mb: 2 }}>
              {header}
            </Heading>
            <Box>{content}</Box>
          </Box>
        ))}
      </Modal>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
      >
        <Button
          onClick={() => setIsOpen(true)}
          variant="link"
          sx={{
            py: [2, null, null, 3, 4],
            px: 3,

            fontSize: [1, null, null, 2],
            fontWeight: 'bold',
          }}
        >
          ?
        </Button>
      </Box>
    </React.Fragment>
  )
}
